import React from 'react';
import { Auth, Hub } from 'aws-amplify';
import PropTypes from 'prop-types';
import { MainHeader, PageLayout, StoreSelection, Tabs, CalendarRange, Modal, Preloader } from 'sharedComponents';
import { BackArrow, FileDocIcon } from 'sharedComponents/Icons';
import { Typography, Box, Icon, IconButton, List, ListItem, Fade } from '@material-ui/core';
import InfiniteScroll from 'react-infinite-scroller';
import { downloadReport, downloadDailyReport } from 'utils';
import { logoutUrl } from 'constants/index';
import { useAddNotification } from 'modules/shell/hooks';

/**
 * Provides view for logged in Audit Users to collect audit reports
 * Actual for users with "audit user" AD group.  G_OSP_R
 * More details in https://neimanmarcus.atlassian.net/browse/OSP-11148
 */

//TODOS: Integrate new API
export const AuditUserOptions = ({
  classes,
  config,
  selectedStoreId,
  linkData,
  getOrderLists,
  retrieveDailyTotalsReport,
  retrieveDailyReceiptReport,
}) => {
  const scrollParentRef = React.createRef(null);
  const [loading, setLoading] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);
  const [linksArray, setLinksArray] = React.useState(linkData || []);
  const isModalOpen = linksArray.length > 0;

  const [receiptRange, setReceiptRange] = React.useState({
    key: 'selection',
    startDate: new Date(),
    endDate: new Date(),
  });
  const [totalsRange, setTotalsRange] = React.useState({
    key: 'selection',
    startDate: new Date(),
    endDate: new Date(),
  });

  const dispatchNotification = useAddNotification();

  const fetchReports = async (range, type) => {
    let reportData = [];

    try {
      setLoading(true);
      if (type === 'receipts') {
        setReceiptRange(range);
        const { dailyOrders = [] } = await getOrderLists({ selectedStoreId, range });
        reportData = dailyOrders[0]?.ordersList || dailyOrders;
        setLinksArray(reportData);
      } else if (type === 'totals') {
        setTotalsRange(range);
        const { dailySummaries = [] } = await retrieveDailyTotalsReport({ selectedStoreId, range });
        reportData = dailySummaries;
        setLinksArray(reportData);
      }
      if (reportData.length === 0) {
        dispatchNotification({ type: 'error', message: 'No Reports Found' });
      }
    } finally {
      setLoading(false);
    }
  };

  const dailyTabContent = () => (
    <Box className={classes.container}>
      <Box className={classes.firstTypography}>
        <Typography variant="h1">Audit Reporting</Typography>
        <Typography variant="body1">Daily Receipt Download</Typography>
      </Box>
      <CalendarRange
        calendarRange={receiptRange}
        onUpdateClicked={(range) => fetchReports(range, 'receipts')}
        buttonAlt="Download Receipts"
        disableBtn={selectedStoreId === null}
        restrictToSingleDate
      />
      <Typography variant="body1" className={classes.dailyTotals}>
        Daily Totals Download
      </Typography>
      <CalendarRange
        calendarRange={totalsRange}
        onUpdateClicked={(range) => fetchReports(range, 'totals')}
        buttonAlt="Download Totals"
        disableBtn={selectedStoreId === null}
      />
    </Box>
  );

  //TODO: To be added as part of separate story. Weekly date selection already enabled
  const weeklyTabContent = () => {
    // <Box className={classes.container}>
    //   <Box className={classes.firstTypography}>
    //     <Typography variant="h1">Audit Reporting</Typography>
    //     <Typography variant="body1">Weekly Download</Typography>
    //   </Box>
    //   <CalendarRange
    //     calendarRange={receiptRange}
    //     onUpdateClicked={(range) => setReceiptRange(range)}
    //     buttonAlt="Download"
    //     restrictToWeek
    //   />
    // </Box>
  };

  const tabs = [
    {
      label: 'Daily',
      tabId: 0,
    },
    {
      label: 'Weekly',
      tabId: 1,
    },
  ];

  const onClear = () => {
    setReceiptRange({ startDate: new Date(), endDate: new Date(), key: 'selection' });
    setTotalsRange({ startDate: new Date(), endDate: new Date(), key: 'selection' });
    setLinksArray([]);
  };

  const onSignOut = () => {
    //NOTE: This if statement is for testing purposes to manually trigger the modal
    // if (!isModalOpen) return setIsModalOpen(true);

    const authConfig = config.get('Auth');
    Hub.dispatch('osp-auth', { signOut: true });
    Auth.signOut();
    window.location.href = logoutUrl(authConfig);
  };

  const renderTitle = () => (
    <Box className={classes.headerContainer}>
      <Box className={classes.headerTitle}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="go back"
          data-testid="go-back"
          onClick={onSignOut}
          classes={{ root: classes.icon }}
        >
          <Icon component={BackArrow} fontSize="small" />
        </IconButton>
        <StoreSelection headerMode selectedStoreId={selectedStoreId} isUniversalSearch />
      </Box>
    </Box>
  );

  const onDownloadClick = async (link, selectedStoreId) => {
    const storeNumber = parseInt(selectedStoreId, 10);
    if (link.total >= 0) {
      return downloadReport(link, storeNumber);
    }

    const { resp } = await retrieveDailyReceiptReport({
      selectedStoreId: storeNumber,
      range: receiptRange,
      orderNumber: link,
    });
    return resp ? downloadDailyReport(resp) : null;
  };

  const loadMoreItems = () => {
    //TODO: Update when API integration is complete
    // setLinksArray([...linksArray, ...linksArray]);
  };

  const renderContent = (currentTab) => {
    switch (currentTab) {
      case 0:
        return dailyTabContent();

      case 1:
        return weeklyTabContent();

      default:
        return null;
    }
  };

  return (
    <>
      <MainHeader customTitle={renderTitle} />
      <PageLayout>
        <Tabs
          tabs={tabs}
          onChange={(_, newValue) => {
            setCurrentTab(newValue);
          }}
          value={currentTab}
          isFixed
          isTopPadded
          tabsClasses={{ root: classes.tabs }}
        />
        {renderContent(currentTab)}
      </PageLayout>
      {loading && <Preloader usePortal />}
      <Modal isOpen={isModalOpen} onClose={onClear}>
        <Fade in={isModalOpen}>
          <>
            <Typography className={classes.modalHeader} variant="h4">
              Reports Found: Total {linksArray.length}
            </Typography>
            <Box className={classes.modalContainer} ref={scrollParentRef}>
              <InfiniteScroll
                data-testid="infinite-scroller"
                initialLoad={false}
                loadMore={loadMoreItems}
                hasMore
                useWindow={false}
              >
                <List className={classes.linkList}>
                  {linksArray.map((link) => (
                    <ListItem className={classes.listItem} key={link.date ? link.date : link}>
                      <Typography variant="body1" className={classes.name}>
                        {link.date ? `Document Date: ${link.date}` : `Transaction : ${link}`}
                      </Typography>
                      <IconButton className={classes.listIcon} onClick={() => onDownloadClick(link, selectedStoreId)}>
                        <Icon component={FileDocIcon} fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </InfiniteScroll>
            </Box>
          </>
        </Fade>
      </Modal>
    </>
  );
};

//TODO: proptypes/default props
AuditUserOptions.propTypes = {
  classes: PropTypes.shape({}),
  config: PropTypes.shape({}),
  selectedStoreId: PropTypes.string,
};

export default AuditUserOptions;
